import { formatCompactNumber, parseCompactNumber } from '@/components/grid/formatters'

/**
 * Parse a typed value into a number, supporting K/M/B/T suffixes
 * ("15k" → 15000) and arbitrary-precision decimals ("0.00000001").
 * Same engine the dashboard filters use. Returns null when unparseable.
 */
export function parseBacktestValue(input: string): number | null {
  return parseCompactNumber(input)
}

/** Strip trailing zeros (and a dangling decimal point) from a fixed-notation string. */
function trimZeros(s: string): string {
  if (!s.includes('.')) return s
  return s.replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * Format a number for at-rest display in backtester inputs.
 *
 * - Large magnitudes (|v| >= 1000) compact to K/M/B/T via the shared formatter
 *   — cash, share counts, volume.
 * - Smaller magnitudes render at full precision (no forced 2-decimal rounding)
 *   so crypto/forex denominations like 0.00000001 or 1.2345 survive intact.
 *   Precision is capped at `maxSigFigs` significant figures to avoid float noise
 *   (e.g. 1/3 → "0.33333333").
 *
 * Note: this is intentionally NOT the shared `formatCompactNumber` — that one
 * rounds to 2 decimals and is relied on by the dashboard grids/filters.
 */
export function formatBacktestValue(value: number, maxSigFigs = 8): string {
  if (!Number.isFinite(value)) return ''
  if (value === 0) return '0'

  const abs = Math.abs(value)
  if (abs >= 1e3) return formatCompactNumber(value)

  // Decimals needed to show `maxSigFigs` significant figures, no sci-notation.
  const exp = Math.floor(Math.log10(abs))
  const decimals = Math.min(100, Math.max(0, maxSigFigs - 1 - exp))
  return trimZeros(value.toFixed(decimals))
}
